import XDate from 'xdate'

import { ONE_DAY_MS } from '../../_utilities/relative-time'
import { CountdownBreakdown } from './types'

/**
 * Builds a countdown breakdown from a target millis timestamp. Total
 * days are computed against the start of the local day so a target set
 * "today" reads as 0 instead of fractional.
 *
 * @param {number} targetMillis - the move-in target timestamp
 * @returns {CountdownBreakdown} the renter-facing countdown breakdown
 */
const buildCountdownBreakdown = (targetMillis: number): CountdownBreakdown => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const target = new Date(targetMillis)
  target.setHours(0, 0, 0, 0)

  const diffMs = target.getTime() - now.getTime()
  const totalDays = Math.round(diffMs / ONE_DAY_MS)
  const weeks = Math.floor(Math.abs(totalDays) / 7)
  const remainingDays = Math.abs(totalDays) % 7

  return {
    totalDays,
    weeks,
    remainingDays,
    isPast: totalDays < 0,
    isToday: totalDays === 0,
  }
}

/**
 * Formats the breakdown into a renter-facing label. Today / past /
 * future variants return distinct copy so the widget never reads
 * generically.
 *
 * @param {CountdownBreakdown} breakdown - the resolved countdown
 * @returns {string} the renter-facing label
 */
const formatCountdownLabel = (breakdown: CountdownBreakdown): string => {
  if (breakdown.isToday === true) {
    return "Move-in day — let's go"
  }

  if (breakdown.isPast === true) {
    const absDays = Math.abs(breakdown.totalDays)
    if (absDays === 1) {
      return 'Moved in yesterday'
    }
    return `Moved in ${absDays} days ago`
  }

  if (breakdown.totalDays === 1) {
    return 'Move-in is tomorrow'
  }

  if (breakdown.weeks === 0) {
    return `${breakdown.totalDays} days until move-in`
  }

  if (breakdown.remainingDays === 0) {
    const weekWord = breakdown.weeks === 1 ? 'week' : 'weeks'
    return `${breakdown.weeks} ${weekWord} until move-in`
  }

  const weekWord = breakdown.weeks === 1 ? 'wk' : 'wks'
  const dayWord = breakdown.remainingDays === 1 ? 'day' : 'days'
  return `${breakdown.weeks}${weekWord} ${breakdown.remainingDays}${dayWord} until move-in`
}

/**
 * Picks the supportive secondary line under the countdown number.
 *
 * @param {CountdownBreakdown} breakdown - the resolved countdown
 * @param {number} targetMillis - the move-in target timestamp
 * @returns {string} the secondary hint line
 */
const buildCountdownHint = (
  breakdown: CountdownBreakdown,
  targetMillis: number,
): string => {
  const targetLabel = new XDate(targetMillis).toString('MMM d, yyyy')

  if (breakdown.isToday === true) {
    return `Targeting ${targetLabel}.`
  }

  if (breakdown.isPast === true) {
    return `You set ${targetLabel}. Time to update?`
  }

  if (breakdown.totalDays <= 7) {
    return `Targeting ${targetLabel}. Final stretch — pack the boxes.`
  }

  if (breakdown.totalDays <= 30) {
    return `Targeting ${targetLabel}. One month out — short list those places.`
  }

  return `Targeting ${targetLabel}. Plenty of time to scout.`
}

/**
 * Converts a date-input value (YYYY-MM-DD) into a local-midnight millis
 * timestamp suitable for storing on the profile.
 *
 * @param {string} dateInputValue - the YYYY-MM-DD string from a date input
 * @returns {number | null} the millis timestamp, or null when input is empty
 */
const parseDateInputToMillis = (dateInputValue: string): number | null => {
  if (dateInputValue === '') {
    return null
  }

  const [year, month, day] = dateInputValue.split('-').map(Number)
  if (
    Number.isFinite(year) === false ||
    Number.isFinite(month) === false ||
    Number.isFinite(day) === false
  ) {
    return null
  }

  return new Date(year, month - 1, day).getTime()
}

/**
 * Converts a millis timestamp into the YYYY-MM-DD string a date input
 * expects.
 *
 * @param {number | null} millis - the stored move-in millis
 * @returns {string} the input value, or empty string when null
 */
const formatMillisForDateInput = (millis: number | null): string => {
  if (millis === null) {
    return ''
  }
  const date = new Date(millis)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export {
  buildCountdownBreakdown,
  buildCountdownHint,
  formatCountdownLabel,
  formatMillisForDateInput,
  parseDateInputToMillis,
}
